import { useState, useRef } from "react";

/**
 * Reusable file upload component with drag-drop, preview, and progress.
 *
 * Props:
 *   accept    — file types (e.g. "image/*,application/pdf")
 *   maxSizeMB — max file size in MB (default 5)
 *   onUpload  — async (file) => void — called when file is selected
 *   label     — upload button label
 *   hint      — hint text below label
 *   uploaded  — true if file already uploaded
 *   fileName  — name of uploaded file (for display)
 */
export function FileUpload({ accept = "image/*,application/pdf", maxSizeMB = 5, onUpload, label, hint, uploaded, fileName }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const maxBytes = maxSizeMB * 1024 * 1024;

  async function handleFile(file) {
    setError(null);
    if (!file) return;

    if (file.size > maxBytes) {
      setError(`File too large (max ${maxSizeMB}MB)`);
      return;
    }

    // Preview for images
    if (file.type.startsWith("image/")) {
      const url = URL.createObjectURL(file);
      setPreview(url);
    } else {
      setPreview(null);
    }

    setUploading(true);
    try {
      await onUpload(file);
    } catch (err) {
      setError(err.message ?? "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }

  function handleChange(e) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }

  const borderColor = dragging ? "#3b82f6" : uploaded ? "#16a34a" : "#2d3f5a";
  const bgColor = dragging ? "#1e3a5f20" : "#0d1117";

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      style={{
        border: `2px dashed ${borderColor}`,
        borderRadius: 12,
        padding: "24px 20px",
        textAlign: "center",
        background: bgColor,
        transition: "all .2s",
        cursor: "pointer",
      }}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{ display: "none" }}
      />

      {preview && (
        <img
          src={preview}
          alt="Preview"
          style={{ maxWidth: 200, maxHeight: 150, borderRadius: 8, marginBottom: 12, objectFit: "cover" }}
        />
      )}

      {uploading ? (
        <div style={{ color: "#60a5fa", fontSize: 14 }}>Uploading...</div>
      ) : uploaded ? (
        <div>
          <div style={{ color: "#16a34a", fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            Uploaded
          </div>
          {fileName && <div style={{ color: "#64748b", fontSize: 12 }}>{fileName}</div>}
          <div style={{ color: "#64748b", fontSize: 11, marginTop: 8 }}>Click or drag to replace</div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 28, marginBottom: 8 }}>{label?.includes("ID") ? "🪪" : "📄"}</div>
          <div style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            {label ?? "Upload file"}
          </div>
          <div style={{ color: "#64748b", fontSize: 12 }}>
            {hint ?? `Drag & drop or click to browse. Max ${maxSizeMB}MB`}
          </div>
        </div>
      )}

      {error && (
        <div style={{ color: "#ef4444", fontSize: 12, marginTop: 8 }}>{error}</div>
      )}
    </div>
  );
}
